import { Column } from '@/components/ui/layout/Column';
import { StepType } from '@reactour/tour';
import { Text } from '@/components/ui/base/Text';
import React from 'react';
import { useTheme } from '@/providers/ThemeProvider';
import { Button } from '@/components/ui/Button';

type PopoverStyles = {
  bottom?: string;
  left?: string;
  top?: string;
  right?: string;
  transform?: string;
  backgroundColor?: string;
  color?: string;
  zIndex?: number;
  [key: string]: string | Record<string, string> | number | undefined;
};

const TourStep = ({ children }: { children: React.ReactNode }) => {
  return <Column>{children}</Column>;
};

export function useAnalysisSteps(): StepType[] {
  const { theme } = useTheme();

  const getBaseStyles = (): {
    popover: (base: PopoverStyles) => PopoverStyles;
  } => ({
    popover: (base: PopoverStyles): PopoverStyles => ({
      ...base,
      bottom: '20px',
      left: '20px',
      top: 'inherit',
      right: 'inherit',
      transform: 'none',
      backgroundColor: theme === 'light' ? 'white' : '#161617',
      color:
        theme === 'dark' ? 'var(--color-text-light)' : 'var(--color-text-dark)',
      zIndex: 9999,
    }),
  });

  return [
    {
      selector: '#canvas-detection-controls',
      content: () => (
        <TourStep>
          <Text>
            Voici le viewer de document. Vous pouvez zoomer, déplacer et
            interagir avec le document.
          </Text>
          <Text>
            👉 Appuyez sur la touche "espace" ou sur le bouton en haut à gauche
            du viewer pour rendre visibles les détections.
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['#canvas-container'],
      styles: getBaseStyles(),
    },
    {
      selector: '#canvas-rotation-controls',
      content: () => (
        <TourStep>
          <Text>
            Bravo ! Maintenant essayez de remettre le document à l'endroit.
          </Text>
          <Text>
            👉 Vous pouvez faire pivoter le document en appuyant sur les touches
            "L" ou "R" ou bien en utilisant les boutons de rotation en bas à
            gauche du viewer.
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['#canvas-container'],
      styles: getBaseStyles(),
    },
    {
      selector: '#canvas-actions',
      content: () => (
        <TourStep>
          <Text>
            Vous trouverez en haut à droite du viewer plusieurs boutons
            d'actions.
          </Text>
          <Text>
            👉 Essayez d'ajouter le document à vos favoris en cliquant sur le
            bouton "Ajouter aux favoris".
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['#canvas-container'],
      styles: getBaseStyles(),
    },
    {
      selector: '#canvas-pagination-controls',
      content: () => (
        <TourStep>
          <Text>
            Parfait ! Le document a été ajouté à vos favoris. Vous pourrez le
            retrouver facilement dans la liste des fichiers.
          </Text>
          <Text>
            👉 Naviguez jusqu'à la page 2 du PDF en utilisant les boutons de
            pagination.
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['#canvas-container'],
      styles: getBaseStyles(),
    },

    {
      selector: '#preview-section',
      content: (props) => (
        <TourStep>
          <Text>
            Passons maintenant à la partie analyse. Vous pouvez ici voir les
            différentes informations extraites du document
          </Text>
          <Text>
            Cliquez sur "Metadonnées" et sur "Test" pour voir les informations
            du document et les résultats des tests effectués, puis recliquez
            dessus pour les replier.
          </Text>
          <Button variant="primary" onClick={() => props.setCurrentStep(5)}>
            Suivant
          </Button>
        </TourStep>
      ),
      highlightedSelectors: ['#sidebar-container'],
    },

    {
      selector: '#detections-section',
      content: () => (
        <TourStep>
          <Text>
            Visualisez ici les détections et anomalies identifiées sur chaque
            page, ainsi que celles dont la page n'est pas identifiée dans "Non
            localisables".
          </Text>
          <Text>
            👉 Cliquez sur "MRZ faux" au niveau des détections de la page 2,
            puis sur le bouton "MRZ" pour voir les informations extraites du
            document.
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['#sidebar-container'],
      resizeObservables: ['#sidebar-container'],
    },
    {
      selector: '#mrz-modal',
      content: () => (
        <TourStep>
          <Text>
            Vous avez ici accès à la section MRZ, où vous pouvez voir les
            données extraites du document, le changer à la main avec la modal
            ce-dessus et le re-vérifier.
          </Text>
          <Text>
            👉 Ajoutez un "&lt;" à la fin de la dernière ligne puis cliquez sur
            "Re-vérifier le MRZ" pour voir le résultat.
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['body'],
    },
    {
      selector: '#mrz-modal-close-button',
      content: () => (
        <TourStep>
          <Text>
            Bravo, le MRZ a bien été vérifié. Vous pouvez fermer la fenêtre de
            MRZ en cliquant sur la croix en haut à droite de la fenêtre.
          </Text>
          <Text>
            👉 Cliquez sur la croix en haut à droite de la fenêtre pour fermer
            la fenêtre de MRZ.
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['body'],
    },
    {
      selector: '#canvas-actions',
      content: () => (
        <TourStep>
          <Text>
            Voyons maintenant comment ajouter un commentaire au document.
          </Text>
          <Text>
            👉 Cliquez sur le 3e icône "Notes" pour ouvrir la section de
            commentaires et ajoutez votre texte.
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['body'],
    },
    {
      selector: '#notes-modal',
      content: () => (
        <TourStep>
          <Text>Essayer d'ajouter un commentaire sur le document.</Text>
          <Text>
            👉 Dans la section des commentaires, tapez "valide" et enregistrez
            votre commentaire.
          </Text>
        </TourStep>
      ),
      highlightedSelectors: ['body'],
    },
    {
      selector: '#sidebar-back-button',
      content: () => (
        <TourStep>
          <Text>
            Nous avons fait le tour pour la partie Analyse ! Vous pouvez revenir
            à la liste des documents en cliquant sur ce bouton pour voir la
            dernière information utile.
          </Text>
        </TourStep>
      ),
      position: 'center',
    },
  ];
}
